import { useContext, useEffect, useState } from 'react';
import { Appearance, StripeElementsOptions } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';

import SetupPaymentForm from './setup-payment-form';
import { usePaymentApiV1 } from '../../../../clients/quipli-service/payment/payment-api-v1';
import { StripeContext } from '../../../../context/stripe-context';
import { Spinner } from '../../../../components/spinner/spinner';
import { useCustomer } from '../../../../hooks/use-customer-context';

export const SetupPayment = ({
  color,
  enableCancel,
  onCancel,
  onSubmit,
  type = 'card',
}: {
  color: string;
  enableCancel?: boolean;
  onCancel?: () => void;
  onSubmit: () => void;
  type?: 'bankAccount' | 'card';
}) => {
  const customer = useCustomer();
  const stripe = useContext(StripeContext);
  const { getSetupIntent } = usePaymentApiV1();

  const [clientSecret, setClientSecret] = useState('');
  const [url, setUrl] = useState('');

  async function init() {
    const { setupIntent, url } = await getSetupIntent(type == 'bankAccount');
    setClientSecret(setupIntent.client_secret!);
    setUrl(url);
  }

  useEffect(() => {
    if (!stripe) {
      throw new Error('Stripe was not initialized');
    }
    init();
  }, []);

  const appearance: Appearance = {
    theme: 'stripe',
    variables: {
      colorText: '#1a1c25',
      colorTextPlaceholder: '#b9bbc0',
      colorPrimary: color,
      colorDanger: '#ff5d5d',
      borderRadius: '0.5rem',
      fontFamily: 'Arial, sans-serif',
      fontSmooth: 'always',
      fontSizeBase: '14px',
      fontLineHeight: '1.5',
    },
    rules: {
      '.Input': {
        borderColor: '#d0d5dd',
        boxShadow: 'none',
      },
      '.Input:hover': {
        borderColor: color,
      },
      '.Input:focus': {
        borderColor: color,
        boxShadow: 'none',
      },
      '.Input--invalid': {
        boxShadow: 'none',
      },
      '.Label': {
        color: '#535661',
        fontSize: '11px',
        fontWeight: 'bold',
        lineHeight: '1.6',
      },
    },
  };

  const options: StripeElementsOptions = {
    clientSecret,
    appearance,
  };

  return (
    <div className="SetupPayment">
      {clientSecret && customer ? (
        <Elements options={options} stripe={stripe}>
          <SetupPaymentForm
            baseUrl={url}
            user={customer}
            enableCancel={enableCancel ?? true}
            onCancel={onCancel}
            onSubmit={onSubmit}
            type={type}
          />
        </Elements>
      ) : (
        <Spinner />
      )}
    </div>
  );
};
